import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { IconTriangleUp } from "~/components/icon";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { add } from "~/tools/add";
import { cx } from "~/tools/class-variance";
import { findIndex } from "~/tools/find-index";
import { identity } from "~/tools/identity";
import { pipe } from "~/tools/pipe";
import type { Record } from "~/tools/record";

import { useTableContext } from "./_context";
import { TableHeadCellButton } from "./_head-cell-button";
import { TableColOrder } from "./col-order";

const DEFAULT_COL_ORDER = TableColOrder.init();

const SORTING_TOOLTIP = {
	none: "Unsorted",
	asc: "Sorted in ascending order",
	desc: "Sorted in descending order",
} satisfies Record;

const ICON_SORT_TRIANGLE_CLASS = cx("text-10 transition-colors");

export interface TableHeadCellSorterProps {
	order: undefined | TableColOrder;
	onOrderChange: (order: TableColOrder) => void;
}

export const TableHeadCellSorter: React.FC<TableHeadCellSorterProps> = ({
	order = DEFAULT_COL_ORDER,
	onOrderChange,
}) => {
	const { sortedBy } = useTableContext();
	const priority = useComputed(
		(monitor) => {
			const entries = sortedBy.read(monitor).toArray();

			if (entries.length < 2) {
				return 0;
			}

			return pipe(
				TableColOrder.sort(entries, identity),
				findIndex(([sortedOrder]) => sortedOrder === order),
				add(1),
			);
		},
		[sortedBy, order],
	);

	const tooltip = SORTING_TOOLTIP[order.value ?? "none"];

	React.useEffect(() => {
		sortedBy.write((current) => (order.value == null ? current.delete(order) : current.add(order)));

		return () => {
			sortedBy.write((current) => current.delete(order));
		};
	}, [sortedBy, order]);

	return (
		<Tooltip>
			<TooltipTrigger>
				<TableHeadCellButton
					className="gap-1"
					isSelected={order.value != null}
					onClick={(event) => {
						if (event.shiftKey) {
							onOrderChange(order.prev(Date.now()));
						} else {
							onOrderChange(order.next(Date.now()));
						}
					}}
				>
					<div className="-mt-px -mb-0.5">
						<IconTriangleUp className={cx(ICON_SORT_TRIANGLE_CLASS, order.value === "desc" && "opacity-50")} />

						<IconTriangleUp
							className={cx(ICON_SORT_TRIANGLE_CLASS, "mt-[-3px] rotate-180", order.value === "asc" && "opacity-50")}
						/>
					</div>
					{priority > 0 && <span className="pr-0.5 font-normal text-xs/none">{priority}</span>}
				</TableHeadCellButton>
			</TooltipTrigger>

			<TooltipContent>{priority > 0 ? `${tooltip}, priority ${priority}` : tooltip}</TooltipContent>
		</Tooltip>
	);
};
